import { enableDragToScroll } from "./dragToScroll.js";

class StickyHeader extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.header = this.closest("#shopify-section-header");
    this.predictiveSearch = this.querySelector("predictive-search");
    this.enableStickyHeader = [true, "true"].includes(
      this.dataset.enableStickyHeader,
    );
    this.announcementBar = document.querySelector("announcement-bar");
    this.isTransparent = this.hasAttribute("data-transparent");
    this.dropdownLinks = this.querySelectorAll("[data-expand-on-hover]");
    this.mobileNavExposedWrapper = document.querySelector(
      "[data-mobile-nav-exposed-wrapper",
    );
    this.mobileNavExposed = document.querySelector("[data-mobile-nav-exposed]");
    this.megamenus = this.querySelectorAll("[data-megamenu]");
    this.overlay = this.querySelector("[data-overlay]");
    this.headerOffset =
      document.querySelector("#shopify-section-announcement-bar")
        ?.offsetHeight || 0;

    this.updateRootVar("--header-height", `${this.header.offsetHeight}px`);
    this.setHeaderOffset();
    this.setEventListeners();
  }

  /**
   * Update :root CSS variables for announcement bar & header height.
   * @param {String} variable - Variable name, prefixed with '--'
   * @param {String} value - Value to update it to
   */
  updateRootVar(variable, value) {
    const root = document.querySelector(":root");
    root.style.setProperty(variable, value);
  }

  /**
   * Set event listeners.
   */
  setEventListeners() {
    this.onScrollHandler = this.onScroll.bind(this);

    window.addEventListener("scroll", this.onScrollHandler, false);
    window.addEventListener("resize", this.setHeaderOffset.bind(this));

    this.header.addEventListener("keydown", (event) => {
      if (event.key != "Escape" && event.key != "Esc") {
        return;
      }

      this.closeMenus();
    });

    this.dropdownLinks.forEach((link) => {
      link.addEventListener("mouseenter", (event) => {
        if (link.attributes["aria-expanded"]?.value?.toString() == "true") {
          return;
        }

        this.dropdownOverlayToggle(event.target);
      });

      link
        .querySelector("[data-expand-button]")
        .addEventListener("keydown", (event) => {
          if (event.key != "Enter") {
            return;
          }

          this.dropdownOverlayToggle(link);
        });

      link.addEventListener("mouseleave", (event) =>
        this.dropdownOverlayToggle(event.target),
      );
    });

    this.megamenus.forEach((menu) => {
      menu.addEventListener("focusout", (event) => {
        if (menu.contains(event.relatedTarget)) {
          return;
        }

        const toggle = document.querySelector(`[aria-controls='${menu.id}']`);
        this.dropdownOverlayToggle(toggle);
      });

      menu.addEventListener("mouseleave", this.closeMenus());
    });

    enableDragToScroll(this.mobileNavExposed);
  }

  disconnectedCallback() {
    window.removeEventListener("scroll", this.onScrollHandler);
  }

  setHeaderOffset() {
    if (!this.isTransparent) {
      return;
    }

    let offset = 0;
    const headerInner = this.header.querySelector(".header__inner");
    const headerInnerHeight = headerInner.clientHeight || 0;
    const mobileNavExposedHeight = this.mobileNavExposed?.clientHeight || 0;
    const isAnnouncementBelow =
      this.announcementBar?.classList.contains("announcement-bar--is-bellow") ||
      false;

    offset += headerInnerHeight;
    offset += mobileNavExposedHeight;

    if (isAnnouncementBelow && this.announcementBar) {
      offset += this.announcementBar.clientHeight;
      this.announcementBar.style.top = `${offset}px`;
    }

    this.header.style.marginBottom = `-${offset}px`;
    this.mobileNavExposedWrapper.style.setProperty("top", `${offset}px`);
  }

  onScroll() {
    if (
      !this.enableStickyHeader ||
      this.header.classList.contains("menu-open")
    ) {
      return;
    }

    let bodyTopOffset =
      parseInt(document.body.style.top) === NaN
        ? 0
        : parseInt(document.body.style.top);
    let scrollTop = 0;
    if (bodyTopOffset !== NaN && bodyTopOffset < 0) {
      scrollTop = bodyTopOffset * -1;
    } else {
      scrollTop = window.scrollY;
    }

    if (this.predictiveSearch && this.predictiveSearch.isOpen) return;

    let isScrolledAfter = scrollTop > this.headerOffset;
    const isAnnouncementBelow =
      this.announcementBar?.classList.contains("announcement-bar--is-bellow") ||
      false;

    isAnnouncementBelow
      ? (isScrolledAfter = scrollTop > 0)
      : (isScrolledAfter = scrollTop > this.headerOffset);

    if (isScrolledAfter) {
      requestAnimationFrame(this.reveal.bind(this));
    } else {
      requestAnimationFrame(this.reset.bind(this));
    }

    this.classList.toggle("is-solid", isScrolledAfter && this.isTransparent);
  }

  reveal() {
    const isAnnouncementBelow =
      this.announcementBar?.classList.contains("announcement-bar--is-bellow") ||
      false;
    this.header.classList.add("shopify-section-header-sticky", "animate");
    this.header.classList.remove("shopify-section-header-hidden");

    if (this.announcementBar && !this.isTransparent) {
      this.announcementBar.parentElement.style[
        isAnnouncementBelow ? "paddingTop" : "paddingBottom"
      ] = `${this.header.offsetHeight}px`;
      this.updateRootVar("--announcement-bar-height", "0px");
    }
  }

  reset() {
    const isAnnouncementBelow =
      this.announcementBar?.classList.contains("announcement-bar--is-bellow") ||
      false;

    this.header.classList.remove(
      "shopify-section-header-hidden",
      "shopify-section-header-sticky",
      "animate",
    );

    if (this.announcementBar) {
      this.announcementBar.parentElement.style[
        isAnnouncementBelow ? "paddingTop" : "paddingBottom"
      ] = "0px";
      this.updateRootVar(
        "--announcement-bar-height",
        `${this.announcementBar.offsetHeight}px`,
      );
    }
  }

  closeMenuDisclosure() {
    this.disclosures =
      this.disclosures || this.header.querySelectorAll("details-disclosure");
    this.disclosures.forEach((disclosure) => disclosure.close());
  }

  closeSearchModal() {
    this.searchModal =
      this.searchModal || this.header.querySelector("details-modal");

    if (!this.searchModal) {
      return;
    }

    this.searchModal.close(false);
  }

  /**
   * Close all menus and remove overlay.
   */
  closeMenus() {
    Array.from(this.dropdownLinks).forEach((link) => {
      link.setAttribute[("aria-expanded", false)];
    });

    this.overlay.classList.remove("is-visible");
  }

  /**
   * Toggle megamenu and overlay visibility.
   * @param {Element} toggleElement - Link wrapper that controls the megamenu
   * @returns
   */
  dropdownOverlayToggle(toggleElement) {
    toggleElement.setAttribute(
      "aria-expanded",
      toggleElement.attributes["aria-expanded"]?.value?.toString() == "true"
        ? false
        : true,
    );

    if (!this.overlay) {
      return;
    }

    this.overlay.classList.toggle("is-visible");
  }
}

customElements.define("sticky-header", StickyHeader);
